import { usePromoCodeStore } from "@/stores/promoCode";
import { defineStore } from "pinia";

export const useCartStore = defineStore("cart", {
    state: () => ({
        cartCount: 1,
        cartItems: [],
        cartGUID: null, // GUID для ESputnik tracking
        apiUrl: useRuntimeConfig().public.apiUrl,
        productPackage: [],
        productPackageIds: [],
        totalPriceWithoutDiscount: 0,
        enableSecondItemDiscount: false,
        enableAdditionalDiscount: true,
        productIds: [],
        freeDeliveryThreshold: 3000,
        excludeFromFreeDeliveryIds: [726],
    }),

    getters: {
        changeCartCount: (state) => state.cartCount + 11,

        totalPriceCart(state) {
            const promoCodeState = usePromoCodeStore(); // Pinia store для промокодов

            const total = (state.cartItems || []).reduce((total, item) => {
                const price = item.is_set === 1 ? item.price : (item.variant?.price ?? item.product.price);
                const itemTotal = price * item.quantity;
                const discount = item.discount_amount || 0;
                return total + itemTotal - discount;
            }, 0);

            let totalWithDiscount = total;

            if (promoCodeState.discountType && promoCodeState.discountData) {
                if (promoCodeState.discountType === "PERCENT") {
                    totalWithDiscount = total * (1 - promoCodeState.discountData / 100);
                } else if (promoCodeState.discountType === "AMOUNT") {
                    totalWithDiscount = total - promoCodeState.discountData;
                }
            }

            return Number(Math.max(0, totalWithDiscount).toFixed(2));
        },

        totalOldPriceCart(state) {
            return Number(
                (state.cartItems || [])
                    .reduce((total, item) => {
                        const oldPrice = item.variant?.old_price ?? item.product?.old_price ?? 0;
                        return total + oldPrice * item.quantity;
                    }, 0)
                    .toFixed(2),
            );
        },

        totalDiscountAmount(state) {
            return Number(
                (state.cartItems || [])
                    .reduce((total, item) => {
                        const oldPrice = item.variant?.old_price ?? item.product?.old_price;
                        const price = item.variant?.price ?? item.product.price;
                        const defaultDiscount = oldPrice ? (oldPrice - price) * item.quantity : 0;
                        const additionalDiscount = item.discount_amount || 0;
                        return total + defaultDiscount + additionalDiscount;
                    }, 0)
                    .toFixed(2),
            );
        },

        getProductPackage: (state) => state.productPackage,
        getProductPackageIds: (state) => state.productPackageIds,
        getProductIds: (state) => state.productIds,
        totalPriceWithoutDiscount: (state) => state.totalPriceWithoutDiscount,

        cartItemCount(state) {
            return (state.cartItems || []).reduce((total, item) => total + item.quantity, 0);
        },

        // Геттеры для бесплатной доставки
        totalPriceForFreeDelivery(state) {
            const promoCodeState = usePromoCodeStore();

            // Исключаем товары из списка excludeFromFreeDeliveryIds из расчета для бесплатной доставки
            const total = (state.cartItems || [])
                .filter(item => !state.excludeFromFreeDeliveryIds.includes(item.product_id))
                .reduce((total, item) => {
                    const price = item.is_set === 1 ? item.price : (item.variant?.price ?? item.product.price);
                    const itemTotal = price * item.quantity;
                    const discount = item.discount_amount || 0;
                    return total + itemTotal - discount;
                }, 0);

            let totalWithDiscount = total;

            if (promoCodeState.discountType && promoCodeState.discountData) {
                if (promoCodeState.discountType === "PERCENT") {
                    totalWithDiscount = total * (1 - promoCodeState.discountData / 100);
                } else if (promoCodeState.discountType === "AMOUNT") {
                    totalWithDiscount = total - promoCodeState.discountData;
                }
            }

            return Number(Math.max(0, totalWithDiscount).toFixed(2));
        },

        getProgressWidth(state) {
            const total = this.totalPriceForFreeDelivery;
            return total >= state.freeDeliveryThreshold ? "100%" : `${(total / state.freeDeliveryThreshold) * 100}%`;
        },

        getRemainingAmount(state) {
            const total = this.totalPriceForFreeDelivery;
            return total >= state.freeDeliveryThreshold ? 0 : Math.round(state.freeDeliveryThreshold - total);
        },

        getFreeDeliveryThreshold: (state) => state.freeDeliveryThreshold,
        getExcludeFromFreeDeliveryIds: (state) => state.excludeFromFreeDeliveryIds,

        // Проверяем, есть ли в корзине товары, исключенные из бесплатной доставки
        hasExcludedItemsInCart(state) {
            return (state.cartItems || []).some(item =>
                state.excludeFromFreeDeliveryIds.includes(item.product_id)
            );
        },

        // Геттер для получения текущего GUID корзины
        getCurrentCartGUID: (state) => state.cartGUID,
    },

    actions: {
        // Генерация GUID для ESputnik
        generateGUID() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                const r = Math.random() * 16 | 0
                const v = c === 'x' ? r : (r & 0x3 | 0x8)
                return v.toString(16)
            })
        },

        // ESputnik StatusCart tracking
        trackStatusCart() {
            if (import.meta.client) {
                try {
                    // Используем динамический импорт для избежания SSR проблем
                    import('~/composables/useESputnik').then(({ useESputnik }) => {
                        const { trackStatusCart, isReady } = useESputnik()

                        if (!isReady()) {
                            // Если ESputnik еще не готов, ждем
                            setTimeout(() => this.trackStatusCart(), 1000)
                            return
                        }

                        // Убеждаемся, что GUID существует
                        if (!this.cartGUID) {
                            this.cartGUID = this.generateGUID();
                        }

                        // Преобразуем товары корзины в формат ESputnik
                        const esputnikProducts = this.cartItems.map(item => ({
                            productKey: item.vendor_code ||
                                item.variant?.vendor_code ||
                                item.product?.vendor_code ||
                                String(item.product_id),
                            price: item.is_set === 1 ?
                                item.price :
                                (item.variant?.price ?? item.product?.price),
                            quantity: item.quantity,
                            currency: 'UAH'
                        }))

                        // Отправляем событие StatusCart с текущим GUID
                        trackStatusCart(esputnikProducts, this.cartGUID)
                    }).catch(error => {
                        console.error('[Cart Store] ESputnik tracking error:', error)
                    })
                } catch (error) {
                    console.error('[Cart Store] Failed to track cart status:', error)
                }
            }
        },

        // Обновление корзины БЕЗ отправки ESputnik событий (для скидок, восстановления из localStorage)
        setCartItemsInternal(cartItems) {
            this.cartItems = cartItems;
            localStorage.setItem("cart", JSON.stringify(cartItems));
        },

        updateCartItems(newCartItems) {
            this.cartItems = newCartItems;
            localStorage.setItem("cart", JSON.stringify(newCartItems));

            // Генерируем новый GUID при каждом изменении содержимого корзины
            this.cartGUID = this.generateGUID();
            localStorage.setItem("cartGUID", this.cartGUID);

            // Отправляем событие StatusCart в ESputnik при каждом изменении корзины
            this.trackStatusCart();
        },

        setProductPackage(productPackage) {
            this.productPackage = productPackage;
        },

        addProductPackageIds(productPackageIds) {
            this.productPackageIds = [...new Set([...this.productPackageIds, ...productPackageIds])];
        },

        // Изменено: добавлено логирование productIds и cartItems перед обновлением
        updateProductIds() {
            this.productIds = this.cartItems.map((item) => item.product_id);
        },

        removeCartItem(itemId) {
            this.cartItems = this.cartItems.filter((item) => item.id !== itemId);
            localStorage.setItem("cart", JSON.stringify(this.cartItems));

            // Генерируем новый GUID при удалении товара из корзины
            this.cartGUID = this.generateGUID();
            localStorage.setItem("cartGUID", this.cartGUID);

            // Отправляем обновленное состояние корзины в ESputnik
            this.trackStatusCart();
        },

        setDiscountFlags({ secondItem, additional }) {
            this.enableSecondItemDiscount = secondItem;
            this.enableAdditionalDiscount = additional;
        },

        applyDiscounts() {
            let cartItems = JSON.parse(JSON.stringify(this.cartItems)) || [];

            cartItems.forEach((item) => {
                item.discount_amount = 0;
            });

            const eligibleItems = cartItems.filter((item) => !this.productPackageIds.includes(item.product.id));
            const totalQuantity = eligibleItems.reduce((sum, item) => sum + item.quantity, 0);

            if (this.enableSecondItemDiscount) {
                const discountQuantity = Math.floor(totalQuantity / 2);

                if (discountQuantity > 0) {
                    let sortedItems = eligibleItems.slice().sort((a, b) => {
                        const priceA = a.variant?.price ?? a.product.price;
                        const priceB = b.variant?.price ?? b.product.price;
                        return priceA - priceB;
                    });

                    let remainingDiscountQuantity = discountQuantity;

                    for (let item of sortedItems) {
                        const unitPrice = item.variant?.price ?? item.product.price;
                        const quantityToDiscount = Math.min(item.quantity, remainingDiscountQuantity);
                        const discountPerUnit = unitPrice * 0.444444;
                        item.discount_amount = discountPerUnit * quantityToDiscount;
                        remainingDiscountQuantity -= quantityToDiscount;

                        if (remainingDiscountQuantity <= 0) break;
                    }
                }
            }

            if (this.enableAdditionalDiscount) {
                // price уже содержит скидку 10%, нужно довести итоговую скидку до 15%
                // т.е. нужно вычислить такую дополнительную скидку X, чтобы: (price - X) = originalPrice * 0.85
                // price = originalPrice * 0.9 => originalPrice = price / 0.9
                // (price - X) = (price / 0.9) * 0.85
                // X = price - (price / 0.9) * 0.85

                cartItems.forEach((item) => {
                    // if (totalQuantity == 1) {
                    //     if (!this.productPackageIds.includes(item.product.id) && item.is_set !== 1) {
                    //         const price = item.variant?.price ?? item.product.price;
                    //         const additionalDiscountPerUnit = price - (price / 1.0) * 0.90;
                    //         item.discount_amount += additionalDiscountPerUnit * item.quantity;
                    //     }
                    // }
                    if (totalQuantity >= 2) {
                        if (!this.productPackageIds.includes(item.product.id) && item.is_set !== 1) {
                            const price = item.variant?.price ?? item.product.price;
                            const additionalDiscountPerUnit = price - (price / 0.93) * 0.88;
                            item.discount_amount += additionalDiscountPerUnit * item.quantity;
                        }
                    }
                });
            }

            this.setCartItemsInternal(cartItems);
        },

        fetchCartFromLocalStorage() {
            const cart = localStorage.getItem("cart");
            const savedGUID = localStorage.getItem("cartGUID");

            if (cart) {
                // Загружаем корзину БЕЗ вызова trackStatusCart
                this.cartItems = JSON.parse(cart);

                // Восстанавливаем или создаем GUID
                this.cartGUID = savedGUID || this.generateGUID();

                // Сохраняем GUID если он был создан
                if (!savedGUID) {
                    localStorage.setItem("cartGUID", this.cartGUID);
                }

                this.updateProductIds();
                this.applyDiscounts();
            } else {
                // Если корзина пустая, инициализируем GUID для будущих событий
                this.cartGUID = this.generateGUID();
                localStorage.setItem("cartGUID", this.cartGUID);
            }
        },

        updateCart(newCartItems) {
            this.updateCartItems(newCartItems);
            this.updateProductIds();
            this.applyDiscounts();
        },

        setFreeDeliveryThreshold(threshold) {
            this.freeDeliveryThreshold = threshold;
        },
    },
});
